import React, { useEffect, useId, useMemo, useRef, useState } from "react";

import { AutocompleteInputView } from "./AutocompleteInputView";
import type { AutocompleteInputProps } from "./types";

export type { AutocompleteInputProps } from "./types";

export const AutocompleteInput = ({
  value,
  onChange,
  suggestions,
  id,
  placeholder,
  disabled = false,
  className,
  ariaLabel,
}: AutocompleteInputProps) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [activeSource, setActiveSource] = useState<"keyboard" | "pointer">("keyboard");
  const inputRef = useRef<HTMLInputElement>(null);
  const activeOptionRef = useRef<HTMLDivElement>(null);
  // Tracked separately from `value` so a prefilled field doesn't filter itself
  // down to its own contents on focus-open; null means "nothing typed since focus".
  const [typedQuery, setTypedQuery] = useState<string | null>(null);
  const listboxId = useId();

  const filteredSuggestions = useMemo(() => {
    const query = (typedQuery ?? "").trim().toLowerCase();
    if (!query) return suggestions;
    return suggestions.filter((suggestion) => suggestion.toLowerCase().includes(query));
  }, [suggestions, typedQuery]);

  const isOpen = open && !disabled && filteredSuggestions.length > 0;
  const safeActiveIndex = activeIndex < filteredSuggestions.length ? activeIndex : -1;

  useEffect(() => {
    if (activeSource !== "keyboard") return;
    activeOptionRef.current?.scrollIntoView({ block: "nearest" });
  }, [safeActiveIndex, activeSource]);

  const close = () => {
    setOpen(false);
    setActiveIndex(-1);
    setTypedQuery(null);
  };

  const commit = (option: string) => {
    onChange(option);
    close();
  };

  const handleValueChange = (next: string) => {
    onChange(next);
    setOpen(true);
    setActiveIndex(-1);
    setTypedQuery(next);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      if (isOpen) {
        close();
      }
      return;
    }

    if (event.key === "Tab") {
      close();
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (filteredSuggestions.length === 0) return;
      event.preventDefault();
      const last = filteredSuggestions.length - 1;
      const isDown = event.key === "ArrowDown";

      setActiveSource("keyboard");
      if (!isOpen) {
        setOpen(true);
        setActiveIndex(isDown ? 0 : last);
        return;
      }
      setActiveIndex((current) => {
        const boundedCurrent = current < filteredSuggestions.length ? current : -1;
        if (isDown) return boundedCurrent >= last ? 0 : boundedCurrent + 1;
        return boundedCurrent <= 0 ? last : boundedCurrent - 1;
      });
      return;
    }

    if (event.key === "Enter") {
      if (isOpen && safeActiveIndex >= 0) {
        event.preventDefault();
        commit(filteredSuggestions[safeActiveIndex]);
        return;
      }
      close();
    }
  };

  return (
    <AutocompleteInputView
      value={value}
      filteredSuggestions={filteredSuggestions}
      activeIndex={safeActiveIndex}
      isOpen={isOpen}
      listboxId={listboxId}
      id={id}
      placeholder={placeholder}
      disabled={disabled}
      className={className}
      ariaLabel={ariaLabel}
      inputRef={inputRef}
      activeOptionRef={activeOptionRef}
      onValueChange={handleValueChange}
      onFocus={() => {
        setTypedQuery(null);
        setOpen(true);
      }}
      onClick={() => {
        if (open) return;
        setTypedQuery(null);
        setOpen(true);
      }}
      onBlur={close}
      onKeyDown={handleKeyDown}
      onOptionPick={commit}
      onOptionHover={(index) => {
        setActiveSource("pointer");
        setActiveIndex(index);
      }}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) close();
      }}
    />
  );
};
